<template>
  <div v-if="isHubspotIntegrationEnabled" class="hero-section hero-section__hubspot-container">
    <HubspotCard :hubspotId="HUBSPOT.ID" :href="HUBSPOT.URL" :imgSrc="HUBSPOT.IMG" />
  </div>
  <div v-else class="hero-section">
    <ShopDefaultHeroSection v-if="heroSectionToShow === 'default'" />
    <PremiumMonthlyPromoSection v-if="heroSectionToShow === 'premium-monthly'" />
    <EDAPremiumPreSaleSection v-if="heroSectionToShow === 'eeda-presale'" />
    <BooGoHeroSection v-if="heroSectionToShow === 'boogo'" />
    <BlackFridayHeroSection v-if="heroSectionToShow === 'black-friday'" />
    <LeapYearHeroSection v-if="heroSectionToShow === 'leap-year-2024'" />
    <TCSPromoHeroSection v-if="heroSectionToShow === 'tcs-promo'" />
    <FlashSaleHeroSection v-if="heroSectionToShow === 'flash-sale'" />
    <PremiumPlusPromoHeroSection v-if="heroSectionToShow === 'premium-plus'" />
    <SpringHeroSection v-if="heroSectionToShow === 'spring'" />
    <MaySubscriptionHeroSection v-if="heroSectionToShow === 'may-sale'" />
    <SummerSavingsHeroSection v-if="heroSectionToShow === 'summer-savings'" />
    <MayFlashSaleHeroSection v-if="heroSectionToShow === 'may-flash-sale'" />
    <JulySaleHeroSection v-if="heroSectionToShow === 'july-sale'" />
    <EmaptAndEcthpPreSaleHeroSection v-if="heroSectionToShow === 'emapt-and-ecthp-presale'" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { routesRules } from '@/helpers/mixins'
import PremiumMonthlyPromoSection from '@/components/hero-sections/PremiumMonthlyPromoSection.vue'
import EDAPremiumPreSaleSection from '@/components/hero-sections/EDAPremiumPreSaleSection.vue'
import ShopDefaultHeroSection from '@/components/hero-sections/ShopDefaultHeroSection.vue'
import BooGoHeroSection from '@/components/hero-sections/BooGoHeroSection.vue'
import BlackFridayHeroSection from '@/components/hero-sections/BlackFridayHeroSection.vue'
import LeapYearHeroSection from '@/components/hero-sections/LeapYearHeroSection.vue'
import TCSPromoHeroSection from '@/components/hero-sections/TCSPromoHeroSection.vue'
import FlashSaleHeroSection from '@/components/hero-sections/FlashSaleHeroSection.vue'
import PremiumPlusPromoHeroSection from '@/components/hero-sections/PremiumPlusPromoHeroSection.vue'
import SpringHeroSection from '@/components/hero-sections/SpringHeroSection.vue'
import MaySubscriptionHeroSection from '@/components/hero-sections/MaySubscriptionHeroSection.vue'
import MayFlashSaleHeroSection from '@/components/hero-sections/MayFlashSaleHeroSection.vue'
import SummerSavingsHeroSection from '@/components/hero-sections/SummerSavingsHeroSection.vue'
import JulySaleHeroSection from '@/components/hero-sections/JulySaleHeroSection.vue'
import HubspotCard from '@/components/hero-sections/HubspotCard.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'HeroSection',
  emits: ['hero-section-to-show'],
  mixins: [routesRules],
  components: {
    HubspotCard,
    PremiumMonthlyPromoSection,
    EDAPremiumPreSaleSection,
    ShopDefaultHeroSection,
    BooGoHeroSection,
    BlackFridayHeroSection,
    LeapYearHeroSection,
    TCSPromoHeroSection,
    FlashSaleHeroSection,
    PremiumPlusPromoHeroSection,
    SpringHeroSection,
    MaySubscriptionHeroSection,
    MayFlashSaleHeroSection,
    SummerSavingsHeroSection,
    JulySaleHeroSection,
  },
  data() {
    return {
      heroSectionToShow: 'default',
      HUBSPOT: CONSTANTS.HUBSPOT.HERO_BANNER,
    }
  },
  computed: {
    ...mapState({
      isOnePlanAvailable: state => state.products.isOnePlanAvailable,
      showGenericBackground: state => state.app.showGenericBackground,
      isPremiumMonthlyPromoActive: state => state.app.isPremiumMonthlyPromoActive,
      isEDAPremiumPreSaleActive: state => state.app.isEDAPremiumPreSaleActive,
      isAuthenticated: state => state.auth.isAuthenticated,
      userHasSubscription: state => state.subscriptions.userHasSubscription,
      usersCurrentSubscriptions: state => state.subscriptions.usersCurrentSubscriptions,
    }),
    ...mapGetters({
      isBooGoPromoActive: 'app/getIsBooGoPromoActive',
      isLeapYearPromoActive: 'app/isLeapYearPromoActive',
      isBusinessUser: 'business/isBusinessUser',
      isTCSPromoEnabled: 'featureFlags/isTCSPromoEnabled',
      isFlashSaleActive: 'featureFlags/isFlashSaleActive',
      premiumPlusEnabled: 'featureFlags/premiumPlusEnabled',
      isPremiumPlusEnabledForUser: 'subscriptions/isPremiumPlusEnabledForUser',
      userHasSkillDive: 'subscriptions/userHasSkillDive',
      isSpringPromoEnabled: 'featureFlags/isSpringPromoEnabled',
      isMaySaleEnabled: 'featureFlags/isMaySaleEnabled',
      isMayFlashSaleEnabled: 'featureFlags/isMayFlashSaleEnabled',
      isSummerSavingsEnabled: 'featureFlags/isSummerSavingsEnabled',
      isJulySaleEnabled: 'featureFlags/isJulySaleEnabled',
      isHubspotIntegrationEnabled: 'featureFlags/isHubspotIntegrationEnabled',
    }),
    heroSectionToggler() {
      return [
        {
          name: 'eeda-presale',
          validation: [
            !this.isOnePlanAvailable,
            this.isEDAPremiumPreSaleActive,
            this.isShopPage || this.isEDAPremiumPage,
          ],
        },
        {
          name: 'premium-monthly',
          validation: [
            !this.isOnePlanAvailable,
            this.isPremiumMonthlyPromoActive,
            this.isShopPage || this.isPremiumMonthlyPromoPage,
          ],
        },
        {
          name: 'boogo',
          validation: [
            [
              !this.isAuthenticated,
              !this.isOnePlanAvailable,
              this.isBooGoPromoActive,
              this.isShopPage || this.isBooGoPage,
            ],
            [
              this.isAuthenticated,
              !this.isOnePlanAvailable,
              this.isBooGoPromoActive,
              !(this.usersCurrentSubscriptions.length > 0),
              this.userHasSkillDive,
              this.isShopPage || this.isBooGoPage,
            ],
          ],
        },
        {
          name: 'leap-year-2024',
          validation: [
            !this.isOnePlanAvailable,
            this.isLeapYearPromoActive,
            this.isShopPage || this.isLeapYearPromoPage,
          ],
        },
        {
          name: 'tcs-promo',
          validation: [!this.isOnePlanAvailable, this.isTCSPromoEnabled, this.isShopPage],
        },
        {
          name: 'flash-sale',
          validation: [
            !this.isSummerSavingsEnabled,
            !this.isOnePlanAvailable,
            this.isFlashSaleActive,
            this.isShopPage,
          ],
        },
        {
          name: 'spring',
          validation: [!this.isOnePlanAvailable, this.isSpringPromoEnabled, this.isShopPage],
        },
        {
          name: 'premium-plus',
          validation: [!this.isOnePlanAvailable, this.isPremiumPlusEnabledForUser, this.isShopPage],
        },
        {
          name: 'may-sale',
          validation: [
            !this.isOnePlanAvailable,
            !this.isBusinessUser,
            this.isMaySaleEnabled,
            this.isShopPage,
          ],
        },
        {
          name: 'may-flash-sale',
          validation: [
            !this.isOnePlanAvailable,
            !this.isBusinessUser,
            this.isMayFlashSaleEnabled,
            this.isShopPage,
          ],
        },
        {
          name: 'summer-savings',
          validation: [!this.isOnePlanAvailable, this.isSummerSavingsEnabled, this.isShopPage],
        },
        {
          name: 'july-sale',
          validation: [
            !this.isOnePlanAvailable,
            !this.isBusinessUser,
            this.isJulySaleEnabled,
            this.isShopPage,
          ],
        },
        {
          name: 'default',
          validation: [this.showGenericBackground],
        },
      ]
    },
    watchVariables() {
      return {
        isTCSPromoEnabled: this.isTCSPromoEnabled,
        isAuthenticated: this.isAuthenticated,
        showGenericBackground: this.showGenericBackground,
        isFlashSaleActive: this.isFlashSaleActive,
        isSpringPromoEnabled: this.isSpringPromoEnabled,
        isPremiumPlusEnabledForUser: this.isPremiumPlusEnabledForUser,
        isMaySaleEnabled: this.isMaySaleEnabled,
        isMayFlashSaleEnabled: this.isMayFlashSaleEnabled,
        isSummerSavingsEnabled: this.isSummerSavingsEnabled,
        isJulySaleEnabled: this.isJulySaleEnabled,
      }
    },
  },
  methods: {
    setHeroSectionToShow() {
      const heroHeaderValue = this.heroSectionToggler.find(heroHeader => {
        if (heroHeader.validation[0].length > 1) {
          const validations = heroHeader.validation.find(validationsBlock =>
            validationsBlock.every(v => v === true)
          )
          return !!validations
        }
        return heroHeader.validation.every(v => v === true)
      })
      const heroHeaderOutput = heroHeaderValue ? heroHeaderValue.name : 'default'
      return !this.isShopPage ? '' : heroHeaderOutput
    },
    updateHeroSection() {
      this.heroSectionToShow = this.setHeroSectionToShow()
      this.$emit('hero-section-to-show', this.heroSectionToShow)
    },
  },
  created() {
    this.updateHeroSection()
    this.$watch(
      () => this.watchVariables,
      (newVal, oldVal) => {
        if (newVal !== oldVal) {
          this.updateHeroSection()
        }
      },
      { deep: true }
    )
  },
  beforeUnmount() {
    this.heroSectionToShow = ''
    this.$emit('hero-section-to-show', this.heroSectionToShow)
  },
}
</script>

<style lang="scss" scoped>
.hero-section {
  display: flex;
  justify-content: center;
  align-items: center;
  &__hubspot-container {
    @media screen and (max-width: 1100px) {
      padding: 10px;
    }
    @media screen and (max-width: 880px) {
      padding: 60px 10px 10px 10px;
    }
  }
}
</style>
